import apiClient from '../apiClient';

const mapCategory = (category) => {
  if (!category) return null;
  return {
    id: category.id,
    name: category.name,
    slug: category.slug,
    parentId: category.parent_id ?? null,
    description: category.description || '',
    image: category.image_url || '',
    order: category.order ?? 0,
    isActive: category.is_active ?? true,
  };
};

/**
 * Get all categories
 * @returns {Promise<Array>} Array of categories
 */
export const getAllCategories = async () => {
  try {
    const response = await apiClient.get('/admin/categories');
    return response.data.map(mapCategory);
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

/**
 * Get category by ID
 * @param {string} categoryId - Category ID
 * @returns {Promise<Object>} Category object
 */
export const getCategoryById = async (categoryId) => {
  try {
    const response = await apiClient.get(`/admin/categories/${categoryId}`);
    return mapCategory(response.data);
  } catch (error) {
    console.error('Error fetching category:', error);
    throw error;
  }
};

/**
 * Create a new category
 * @param {Object} categoryData - Category data
 * @returns {Promise<Object>} Created category with ID
 */
export const createCategory = async (categoryData) => {
  try {
    const payload = {
      name: categoryData.name,
      slug: categoryData.slug,
      parent_id: categoryData.parentId || null,
      order: categoryData.order ?? 0,
      image_url: categoryData.image || null,
      description: categoryData.description || null,
      is_active: categoryData.isActive ?? true,
    };
    const response = await apiClient.post('/admin/categories', payload);
    return mapCategory(response.data);
  } catch (error) {
    console.error('Error creating category:', error);
    throw error;
  }
};

/**
 * Update a category
 * @param {string} categoryId - Category ID
 * @param {Object} categoryData - Updated category data
 * @returns {Promise<void>}
 */
export const updateCategory = async (categoryId, categoryData) => {
  try {
    const payload = {
      name: categoryData.name,
      slug: categoryData.slug,
      parent_id: categoryData.parentId || null,
      order: categoryData.order ?? 0,
      image_url: categoryData.image || null,
      description: categoryData.description || null,
      is_active: categoryData.isActive ?? true,
    };
    await apiClient.put(`/admin/categories/${categoryId}`, payload);
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

/**
 * Delete a category
 * @param {string} categoryId - Category ID
 * @param {string} imageUrl - Category image URL to delete from storage
 * @returns {Promise<void>}
 */
export const deleteCategory = async (categoryId, imageUrl = null) => {
  try {
    await apiClient.delete(`/admin/categories/${categoryId}`);
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

/**
 * Bulk delete categories
 * @param {Array<number>} categoryIds - Category IDs
 * @returns {Promise<{deletedIds: Array<number>, errors: Array<string>}>}
 */
export const bulkDeleteCategories = async (categoryIds) => {
  try {
    const response = await apiClient.post('/admin/categories/bulk-delete', { ids: categoryIds });
    return {
      deletedIds: response.data?.deleted_ids || [],
      errors: response.data?.errors || [],
    };
  } catch (error) {
    console.error('Error bulk deleting categories:', error);
    throw error;
  }
};

/**
 * Upload category image
 * @param {File} file - Image file
 * @param {string} categoryId - Category ID
 * @returns {Promise<string>} Download URL
 */
export const uploadCategoryImageFile = async (file, categoryId) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/admin/categories/${categoryId}/image`, formData);
    return response.data?.image_url || response.data?.imageUrl || response.data;
  } catch (error) {
    console.error('Error uploading category image:', error);
    throw error;
  }
};

/**
 * Get product count for a category
 * Note: For better performance with subcategories, consider maintaining a count field
 * or using database-side aggregation queries (requires a plan upgrade)
 * @param {string} categoryId - Category ID
 * @returns {Promise<number>} Product count
 */
export const getCategoryProductCount = async (categoryId) => {
  try {
    const response = await apiClient.get(`/admin/categories/${categoryId}/product-count`);
    return response.data?.product_count ?? 0;
  } catch (error) {
    console.error('Error getting category product count:', error);
    return 0;
  }
};
